import { useEffect, useState } from 'react';
import { Check, Copy, ExternalLink, Plus, X } from 'lucide-react';
import {
    buildRunningOrderText,
    formatTimecode,
    parseRunningOrder,
    parseTimecode,
    soundCloudUrlAt,
    type HappeningArtist,
    type ShowSet,
} from '@littlebooker/dal';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

type Props = {
    value: ShowSet[];
    onChange: (sets: ShowSet[]) => void;
    /** The show's artists; sets pick from these. */
    artists: HappeningArtist[];
    /** The full-show SoundCloud link, used to preview each set's start. */
    soundcloudUrl?: string;
};

const byStart = (sets: ShowSet[]) => [...sets].sort((a, b) => a.start_seconds - b.start_seconds);

/**
 * Edits where each artist's set starts inside a takeover's single full-show recording.
 * Times are offsets into the recording, not clock times, so they line up with
 * SoundCloud's own timestamps.
 */
export function RunningOrderEditor({ value, onChange, artists, soundcloudUrl }: Props) {
    const [pasteText, setPasteText] = useState('');
    const [parseNote, setParseNote] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);

    const update = (index: number, patch: Partial<ShowSet>) =>
        onChange(value.map((s, i) => (i === index ? { ...s, ...patch } : s)));

    const toggleArtist = (index: number, personId: string) => {
        const ids = value[index].person_ids;
        update(index, { person_ids: ids.includes(personId) ? ids.filter((id) => id !== personId) : [...ids, personId] });
    };

    const addSet = () => {
        const last = byStart(value).at(-1);
        // Default the new set an hour after the last one; takeover slots are usually around that long.
        onChange([...value, { start_seconds: last ? last.start_seconds + 3600 : 0, person_ids: [], label: null, url: null }]);
    };

    const applyPaste = () => {
        const { sets, skipped, unmatched } = parseRunningOrder(pasteText, artists);
        if (sets.length === 0) {
            setParseNote('No lines with a start time found. Use one per line, e.g. "0:58:30 DJ Name".');
            return;
        }
        onChange(sets);
        setPasteText('');
        const notes = [];
        if (unmatched.length) notes.push(`Kept as labels (no matching artist on this show): ${unmatched.join(', ')}`);
        if (skipped.length) notes.push(`Skipped lines without a time: ${skipped.length}`);
        setParseNote(notes.length ? notes.join(' · ') : null);
    };

    const copyText = async () => {
        await navigator.clipboard.writeText(buildRunningOrderText(value, artists));
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    const uncovered = artists.filter((a) => !value.some((s) => s.person_ids.includes(a.person_id)));

    return (
        <div className="space-y-3">
            {value.length > 0 && (
                <div className="space-y-2">
                    {value.map((set, index) => (
                        <div key={index} className="rounded-md border p-3 space-y-2">
                            <div className="flex items-center gap-2">
                                <TimecodeInput
                                    seconds={set.start_seconds}
                                    onCommit={(start_seconds) =>
                                        onChange(byStart(value.map((s, i) => (i === index ? { ...s, start_seconds } : s))))
                                    }
                                />
                                <input
                                    className="flex-1 rounded-md border border-input bg-background px-2 py-1 text-sm"
                                    placeholder="Label (optional, e.g. Collective intro)"
                                    value={set.label ?? ''}
                                    onChange={(e) => update(index, { label: e.target.value || null })}
                                />
                                {soundcloudUrl && (
                                    <a
                                        href={soundCloudUrlAt(set.url || soundcloudUrl, set.url ? 0 : set.start_seconds)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-muted-foreground hover:text-primary"
                                        title="Check this start on SoundCloud"
                                    >
                                        <ExternalLink className="h-4 w-4" />
                                    </a>
                                )}
                                <button
                                    type="button"
                                    onClick={() => onChange(value.filter((_, i) => i !== index))}
                                    className="text-muted-foreground hover:text-destructive"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                                {artists.map((a) => {
                                    const on = set.person_ids.includes(a.person_id);
                                    return (
                                        <button
                                            key={a.person_id}
                                            type="button"
                                            onClick={() => toggleArtist(index, a.person_id)}
                                            className={cn(
                                                'rounded-full border px-2 py-0.5 text-xs',
                                                on ? 'border-primary bg-primary text-primary-foreground' : 'border-input hover:bg-accent',
                                            )}
                                        >
                                            {a.name || 'Unnamed artist'}
                                        </button>
                                    );
                                })}
                            </div>
                            <input
                                className="w-full rounded-md border border-input bg-background px-2 py-1 text-xs"
                                placeholder="Separate upload of just this set (optional)"
                                value={set.url ?? ''}
                                onChange={(e) => update(index, { url: e.target.value || null })}
                            />
                        </div>
                    ))}
                </div>
            )}

            {value.length > 0 && uncovered.length > 0 && (
                <p className="text-xs text-amber-600">
                    Not in the running order yet: {uncovered.map((a) => a.name || 'Unnamed artist').join(', ')}
                </p>
            )}

            <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" onClick={addSet}>
                    <Plus className="h-4 w-4 mr-1" />
                    Add set
                </Button>
                {value.length > 0 && (
                    <Button type="button" variant="outline" onClick={copyText}>
                        {copied ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
                        {copied ? 'Copied' : 'Copy for SoundCloud'}
                    </Button>
                )}
            </div>

            <div className="space-y-2">
                <textarea
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    rows={3}
                    placeholder={'Or paste the running order, one set per line:\n0:00 Collective intro\n0:58:30 DJ Name\n1:45:00 DJ Two b2b DJ Three'}
                    value={pasteText}
                    onChange={(e) => setPasteText(e.target.value)}
                />
                <Button type="button" variant="outline" disabled={!pasteText.trim()} onClick={applyPaste}>
                    {value.length > 0 ? 'Replace with pasted running order' : 'Use pasted running order'}
                </Button>
                {parseNote && <p className="text-xs text-muted-foreground">{parseNote}</p>}
            </div>
        </div>
    );
}

/** A free-text timecode field that only commits a value once it parses, on blur. */
function TimecodeInput({ seconds, onCommit }: { seconds: number; onCommit: (seconds: number) => void }) {
    const [draft, setDraft] = useState(formatTimecode(seconds));
    useEffect(() => setDraft(formatTimecode(seconds)), [seconds]);
    const parsed = parseTimecode(draft);
    return (
        <input
            className={cn(
                'w-24 rounded-md border bg-background px-2 py-1 text-sm font-mono',
                parsed === null ? 'border-destructive' : 'border-input',
            )}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => (parsed === null ? setDraft(formatTimecode(seconds)) : onCommit(parsed))}
            aria-label="Set start in the recording"
        />
    );
}
